import { PayStatus } from "../types/player";
import BaseApi from "./Api";
import { getStripeRequestTimeoutMs } from "../config";

class PaymentsApi extends BaseApi {
  url = "payments";

  /**
   * Stripe routes (game checkout, profile Connect, confirm after redirect).
   * Timeout is also applied globally in httpService for any `/stripe/*` URL.
   */
  private stripeRequest(request: { method: string; url: string; data?: unknown }) {
    return this.instance({
      ...request,
      timeout: getStripeRequestTimeoutMs(),
    });
  }

  constructor() {
    super();
    this.setResourceUrl(this.url);
  }
  verifyPayment(id: string, data: any) {
    return this.instance({
      method: "put",
      url: `${this.url}/${id}`,
      data,
    });
  }
  getWithdraws = (page: number, limit: number, filter?: any, sort?: string, dir?: string, query?: string) => {
    let url = `/withdraws?page=${page}&limit=${limit}`;
    if (sort) {
      if (dir) {
        url += `&sort=${sort}:${dir}`;
      } else {
        url += `&sort=${sort}:asc`;
      }
    }
    if (query) {
      url += `&query=${query}`;
    }
    if (filter) {
      url += `&filter=${JSON.stringify(filter)}`;
    }
    url = this.url + url;
    return this.instance({
      method: "get",
      url,
    });
  };
  onboarding(redirectUrl: string): any {
    return this.instance({
      method: "post",
      url: `${this.url}/onboarding`,
      data: { redirectUrl },
    });
  }
  paymentSession(data: any): any {
    return this.instance({
      method: "post",
      url: `${this.url}/paymentSession`,
      data,
    });
  }
  addWithoutPay(
    gameId: string,
    playersId: string[],
    type: PayStatus,
    opts?: { creditIds?: string[] },
  ) {
    return this.instance({
      method: "put",
      url: `${this.url}/${gameId}/paid`,
      data: {
        playersId,
        type,
        creditIds: opts?.creditIds,
      },
    });
  }

  getOpenGameCredits(gameId: string, playerIds?: string[]) {
    const params = [`gameId=${encodeURIComponent(gameId)}`, `_=${Date.now()}`];
    if (playerIds?.length) params.push(`playerIds=${encodeURIComponent(playerIds.join(","))}`);
    return this.instance({
      method: "get",
      url: `${this.url}/game-credits?${params.join("&")}`,
    });
  }
  createStripeCheckoutSession(data: { game: string | { _id: string }; players: string[]; payBeforeJoin?: boolean }) {
    return this.stripeRequest({
      method: "post",
      url: "stripe/create-checkout-session",
      data: {
        game: typeof data.game === "string" ? data.game : data.game?._id,
        players: data.players,
        payBeforeJoin: data.payBeforeJoin,
      },
    });
  }
  confirmStripeSession(sessionId: string) {
    return this.stripeRequest({
      method: "post",
      url: "stripe/confirm-session",
      data: { sessionId },
    });
  }

  cancelStripeSession(sessionId: string) {
    return this.stripeRequest({
      method: "post",
      url: "stripe/cancel-session",
      data: { sessionId },
    });
  }

  getStripeConnectStatus() {
    return this.stripeRequest({
      method: "get",
      url: "stripe/connect/status",
    });
  }

  getStripeConnectStatusForGame(gameId: string) {
    return this.stripeRequest({
      method: "get",
      url: `stripe/connect/status/game/${gameId}`,
    });
  }

  createStripeConnectAccountLink(refreshUrl: string, returnUrl: string) {
    return this.stripeRequest({
      method: "post",
      url: "stripe/connect/account-link",
      data: { refreshUrl, returnUrl },
    });
  }

  createStripeConnectDashboardLink() {
    return this.stripeRequest({
      method: "post",
      url: "stripe/connect/dashboard-link",
    });
  }

  getGamePaymentSummary(gameId: string) {
    return this.instance({
      method: "get",
      url: `${this.url}/games/${gameId}/summary`,
    });
  }
}

export default new PaymentsApi();
